import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context";

const ClientSearchDialog = ({ open, onClose, onSelectClient, token }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const timerRef = useRef(null);

  // Client search API call
  const {
    data: clientData,
    isLoading: isLoadingClient,
    isError: isErrorClient,
    refetch: refetchClient,
  } = useFetchObjects(
    ["search-client"],
    `all-clients/?search=${searchTerm}`,
    token,
    { enabled: false }
  );

  // Debounced search effect
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (searchTerm.trim().length >= 3) {
      setIsSearching(true);
      timerRef.current = setTimeout(() => {
        refetchClient();
        setIsSearching(false);
      }, 500);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [searchTerm, refetchClient]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
  };

  const handleConfirmSelection = () => {
    if (selectedClient && onSelectClient) {
      onSelectClient(selectedClient);
    }
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedClient(null);
    onClose();
  };

  const formatPhoneForSearch = (phone) => {
    if (phone.startsWith("+")) return phone;
    if (phone.startsWith("0")) return phone.substring(1);
    return phone;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Search for Existing Client
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search by name, phone number, or contact to find existing clients
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Search Client"
            placeholder="Enter name, phone number, or contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Box>

        {isSearching && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {isErrorClient && (
          <Alert severity="error">
            Error searching for clients. Please try again.
          </Alert>
        )}

        {clientData && clientData.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Found {clientData.length} client(s):
            </Typography>
            <List>
              {clientData.map((client) => (
                <ListItem key={client.id} disablePadding>
                  <ListItemButton
                    selected={selectedClient?.id === client.id}
                    onClick={() => handleClientSelect(client)}
                  >
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle1">
                            {client.full_name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                            <Chip
                              label={`Phone: ${client.contact}`}
                              size="small"
                              variant="outlined"
                            />
                            {client.contact_2 && (
                              <Chip
                                label={`Alt: ${client.contact_2}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            <Chip
                              label={`Age: ${client.age}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Gender: ${
                                client.gender === 1 ? "Male" : "Female"
                              }`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          {client.follow_up_by && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Follow-up by: {client.follow_up_by.first_name}{" "}
                              {client.follow_up_by.last_name}
                            </Typography>
                          )}
                          {client.status && (
                            <Typography variant="body2" color="text.secondary">
                              Status: {client.status.name}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {clientData &&
          clientData.length === 0 &&
          searchTerm.trim().length >= 3 &&
          !isSearching && (
            <Alert severity="info">
              No clients found matching your search criteria.
            </Alert>
          )}

        {searchTerm.trim().length < 3 && searchTerm.trim().length > 0 && (
          <Alert severity="info">
            Please enter at least 3 characters to search.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          disabled={!selectedClient}
        >
          Select Client
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientSearchDialog;
